// Poofiz kit: the pricing and cart rules, in one place (window.PoofizKit).
// The UI (assets/cozy-kit-builder.js) only collects the choices and calls add().
//
// - Price: the kit is ONE line of the kit product, so the price is that product's own Shopify
//   price for any combination. The browser never sends or sets a price.
// - Stock isn't synced automatically, so add() first re-reads the kit and every chosen style from
//   Shopify (/products/<handle>.js) and refuses anything no longer available. That re-read is the one
//   round trip before cart/add.js: the builder warms it (prefetch) once every step is chosen, and add()
//   uses a read up to a minute old (the cart re-checks every kit line anyway).
// - Line item properties, per slot (slot names come from Theme settings > Poofiz kit):
//     visible  "<slot name>": "<product title> · <variant title>"
//     hidden   "_<slot key>_variant_id": "<variant id>"   (hoodie / slippers / gloves)
//   The cart checks those ids again on every render (snippets/cozy-kit-line-check.liquid).
(() => {
  if (window.PoofizKit) return;

  const root = () => window.Shopify?.routes?.root || '/';
  const cartAddUrl = () => `${window.routes?.cart_add_url || `${root()}cart/add`}.js`;

  // null = the product isn't on the storefront any more (404). Network errors throw.
  async function fetchProduct(handle) {
    const res = await fetch(`${root()}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // One read per handle per minute, shared by prefetch() and verify(); a failed read is forgotten.
  const FRESH_MS = 60000;
  const reads = new Map();
  function readProduct(handle) {
    const hit = reads.get(handle);
    if (hit && performance.now() - hit.at < FRESH_MS) return hit.promise;
    const promise = fetchProduct(handle).catch((err) => {
      reads.delete(handle);
      throw err;
    });
    reads.set(handle, { at: performance.now(), promise });
    return promise;
  }

  const handlesOf = (kit, picks) => [...new Set([kit.handle, ...picks.map((pick) => pick.handle)])];

  // Start the availability read ahead of the add (no result, no error: add() reads again if it failed).
  function prefetch(kit, picks) {
    handlesOf(kit, picks).forEach((h) => readProduct(h).catch(() => {}));
  }

  // "<product title> · <variant title>"; single-variant products show the title only.
  function pickText(pick) {
    return pick.single ? pick.title : `${pick.title} · ${pick.variant.title}`;
  }

  function properties(picks) {
    const props = {};
    picks.forEach((pick) => {
      props[pick.label] = pickText(pick);
    });
    picks.forEach((pick) => {
      props[`_${pick.key}_variant_id`] = String(pick.variant.id);
    });
    return props;
  }

  // Returns the slot keys that can't be bought right now ('kit' = the kit product itself).
  async function verify(kit, picks) {
    const handles = handlesOf(kit, picks);
    const products = new Map(await Promise.all(handles.map(async (h) => [h, await readProduct(h)])));
    const problems = [];
    const kitVariant = products.get(kit.handle)?.variants.find((v) => v.id === kit.variantId);
    if (!kitVariant?.available) problems.push('kit');
    picks.forEach((pick) => {
      const variant = products.get(pick.handle)?.variants.find((v) => v.id === pick.variant.id);
      if (!variant?.available) problems.push(pick.key);
    });
    return problems;
  }

  // picks: [{ key, label, handle, title, single, variant: { id, title } }] in slot order.
  // Resolves { ok: true, data } (the cart/add.js response, incl. rendered sections) or
  // { ok: false, problems }. Throws on network/cart errors; err.userMessage is set only when
  // Shopify itself explains the refusal (cart/add.js description), which is safe to show.
  async function add({ kit, picks, sections = [], sectionsUrl = window.location.pathname }) {
    if (picks.some((pick) => !pick.variant)) return { ok: false, problems: picks.filter((p) => !p.variant).map((p) => p.key) };
    const problems = await verify(kit, picks);
    if (problems.length) return { ok: false, problems };

    const res = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: kit.variantId, quantity: 1, properties: properties(picks) }],
        sections,
        sections_url: sectionsUrl,
      }),
    });
    let data;
    try {
      data = await res.json();
    } catch {
      throw new Error(`HTTP ${res.status}`);
    }
    if (!res.ok || data.status) {
      const err = new Error(data.message || `HTTP ${res.status}`);
      if (typeof data.description === 'string') err.userMessage = data.description;
      throw err;
    }
    return { ok: true, data };
  }

  window.PoofizKit = { add, verify, prefetch, properties, pickText };
})();
